import React, { useState } from "react";
import {
  XMarkIcon,
  DocumentArrowDownIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import * as XLSX from "xlsx";
import { Customer } from "../../services/api";

// Available columns for export
const EXPORT_COLUMNS = [
  { key: "customerId", label: "Customer ID", default: true },
  { key: "firstName", label: "First Name", default: true },
  { key: "lastName", label: "Last Name", default: true },
  { key: "email", label: "Email", default: true },
  { key: "phonePrefix", label: "Phone Prefix", default: false },
  { key: "phoneNumber", label: "Phone Number", default: true },
  { key: "country", label: "Country", default: true },
  { key: "zipCode", label: "ZIP Code", default: false },
  { key: "city", label: "City", default: false },
  { key: "street", label: "Street", default: false },
  { key: "houseNumber", label: "House Number", default: false },
  { key: "addressAddition", label: "Address Addition", default: false },
  { key: "currency", label: "Currency", default: false },
  { key: "status", label: "Status", default: true },
  { key: "sponsorId", label: "Sponsor ID", default: false },
  { key: "sponsorType", label: "Sponsor Type", default: false },
  {
    key: "customerIdSalesBackoffice",
    label: "Sales Backoffice ID",
    default: false,
  },
  { key: "bankName", label: "Bank Name", default: false },
  { key: "iban", label: "IBAN", default: false },
  { key: "bic", label: "BIC", default: false },
  { key: "privacyCheck", label: "Privacy Accepted", default: false },
  { key: "privacyAcceptedAt", label: "Privacy Accepted At", default: false },
  {
    key: "communicationConsent",
    label: "Communication Consent",
    default: false,
  },
  { key: "createdAt", label: "Created At", default: true },
  { key: "updatedAt", label: "Updated At", default: false },
] as const;

type ColumnKey = (typeof EXPORT_COLUMNS)[number]["key"];

interface ExportCustomersModalProps {
  isOpen: boolean;
  onClose: () => void;
  customers: Customer[];
  totalCustomers: number;
  isLoadingAll: boolean;
  onLoadAllCustomers: () => Promise<Customer[]>;
}

const ExportCustomersModal: React.FC<ExportCustomersModalProps> = ({
  isOpen,
  onClose,
  customers,
  totalCustomers,
  isLoadingAll,
  onLoadAllCustomers,
}) => {
  const [selectedColumns, setSelectedColumns] = useState<Set<ColumnKey>>(
    new Set(EXPORT_COLUMNS.filter((c) => c.default).map((c) => c.key)),
  );
  const [exportScope, setExportScope] = useState<"current" | "all">("all");
  const [isExporting, setIsExporting] = useState(false);

  const toggleColumn = (key: ColumnKey) => {
    setSelectedColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedColumns(new Set(EXPORT_COLUMNS.map((c) => c.key)));
  };

  const selectNone = () => {
    setSelectedColumns(new Set());
  };

  const selectDefaults = () => {
    setSelectedColumns(
      new Set(EXPORT_COLUMNS.filter((c) => c.default).map((c) => c.key)),
    );
  };

  const formatValue = (value: any, key: string): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (key.includes("At") || key === "createdAt" || key === "updatedAt") {
      try {
        return new Date(value).toLocaleString();
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const handleExport = async () => {
    if (selectedColumns.size === 0) return;

    setIsExporting(true);
    try {
      let dataToExport = customers;

      if (exportScope === "all" && totalCustomers > customers.length) {
        dataToExport = await onLoadAllCustomers();
      }

      // Prepare data for export
      const exportData = dataToExport.map((customer) => {
        const row: Record<string, string> = {};
        EXPORT_COLUMNS.forEach((col) => {
          if (selectedColumns.has(col.key)) {
            row[col.label] = formatValue(
              customer[col.key as keyof Customer],
              col.key,
            );
          }
        });
        return row;
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map((key) => ({
        wch: Math.max(
          key.length,
          ...exportData.map((row) => (row[key] || "").toString().length),
        ),
      }));
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, "Customers");

      // Generate filename with date
      const date = new Date().toISOString().split("T")[0];
      const filename = `customers_export_${date}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);
      onClose();
    } catch (error) {
      console.error("Export failed:");
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <DocumentArrowDownIcon className="h-6 w-6 text-white" />
              <h3 className="text-lg font-semibold text-white">
                Export Customers to Excel
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {/* Export Scope */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Export Scope
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="all"
                    checked={exportScope === "all"}
                    onChange={() => setExportScope("all")}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    All Customers ({totalCustomers})
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="current"
                    checked={exportScope === "current"}
                    onChange={() => setExportScope("current")}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Current Page ({customers.length})
                  </span>
                </label>
              </div>
            </div>

            {/* Column Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Columns ({selectedColumns.size} selected)
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={selectAll}
                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    Select All
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={selectNone}
                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    None
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={selectDefaults}
                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    Defaults
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {EXPORT_COLUMNS.map((column) => (
                  <label
                    key={column.key}
                    className={`flex items-center p-2 rounded-md cursor-pointer transition-colors ${
                      selectedColumns.has(column.key)
                        ? "bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700"
                        : "bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded flex items-center justify-center mr-2 ${
                        selectedColumns.has(column.key)
                          ? "bg-primary-600 text-white"
                          : "bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500"
                      }`}
                    >
                      {selectedColumns.has(column.key) && (
                        <CheckIcon className="h-3 w-3" />
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedColumns.has(column.key)}
                      onChange={() => toggleColumn(column.key)}
                      className="sr-only"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {column.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={
                selectedColumns.size === 0 || isExporting || isLoadingAll
              }
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isExporting || isLoadingAll ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {isLoadingAll ? "Loading..." : "Exporting..."}
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Export to Excel
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportCustomersModal;
